// src/utils/api.js

import { safeCapture } from "./analytics";
import { getProfile } from "./profile";
import { getAuthHeaders, jsonAuthHeaders } from "./authHeaders";
import { createCaptionBrandsClient, createSocialCaptionsClient } from "./captionsSocial";

// Base URL for the Worker API
const API_BASE =
  import.meta.env.VITE_API_BASE ||
  "https://dobiecore-remix.melanie-brown.workers.dev/api";

// Remix (captions) endpoint
const API_ENDPOINT = `${API_BASE}/remix`;
const BLOG_ENDPOINT = `${API_BASE}/blog`;
const PREWRITING_ENDPOINT = `${API_BASE}/prewriting`;
const IMAGES_ENDPOINT = `${API_BASE}/images`;

async function attachBrandVoice(data = {}) {
  try {
    const profile = await getProfile();

    return {
      ...data,
      brand_voice: data?.brand_voice ?? profile?.brand_voice ?? "",
      brand_profile: data?.brand_profile ?? profile?.active_voice_profile ?? null,
    };
  } catch (error) {
    console.warn("Could not load brand profile for API request:", error);
    return {
      ...data,
      brand_voice: data?.brand_voice ?? "",
      brand_profile: data?.brand_profile ?? null,
    };
  }
}

const ASSISTANT_ENDPOINT = `${API_BASE}/assistant`;
const WELCOME_ENDPOINT = `${API_BASE}/welcome`;

// -----------------------------
// WELCOME EMAIL (W2-R1)
// -----------------------------
//
// Tells the worker "this account just had a verified session". The request
// carries only the caller's Supabase access token: no email, no user id, no
// confirmation flag. The worker derives all of those from Supabase itself,
// decides whether the account is eligible, and makes sure the email goes out at
// most once -- so calling this again is harmless. Until the worker's
// WELCOME_EMAIL_MODE is "on" it sends nothing. Best effort: a failure here must
// never affect sign-in. Resolves to true only when the worker accepted the
// request (202), so the caller can try again on a later auth event otherwise.
export async function requestWelcomeEmail() {
  try {
    const headers = await getAuthHeaders();
    if (!headers.Authorization) return false;
    const response = await fetch(WELCOME_ENDPOINT, { method: "POST", headers });
    return response.status === 202;
  } catch {
    // Ignored on purpose: a later auth event asks again.
    return false;
  }
}

// -----------------------------
// ASSISTANT (customer plane -> Bifrost)
// -----------------------------
//
// The first customer-facing feature served by Bifrost rather than by a direct
// OpenAI call. Everything the browser sends is a message: no model, no route,
// no workspace and no role. Those are resolved server-side by the worker from
// the caller's verified Supabase identity, because they are the facts a
// browser must never be trusted to assert.
//
// Reuses jsonAuthHeaders() so the caller's short-lived Supabase access token
// is attached immediately before the request, the same as every other call
// here. The worker exchanges that for a signed one-hop assertion to Bifrost.
export async function askAssistant(message) {
  const response = await fetch(ASSISTANT_ENDPOINT, {
    method: "POST",
    headers: await jsonAuthHeaders(),
    body: JSON.stringify({ message }),
  });

  if (!response.ok) {
    let body;
    try {
      body = await response.json();
    } catch {
      body = {};
    }
    // The worker already reduces Bifrost's internal rejection reasons to a
    // small set; surface that rather than inventing a friendlier fiction
    // about what went wrong.
    throw new Error(body?.error || `Assistant request failed (${response.status})`);
  }

  const data = await response.json();
  if (typeof data?.answer !== "string" || !data.answer) {
    throw new Error("Assistant returned an empty response");
  }
  return data.answer;
}

// -----------------------------
// CAPTIONS
// -----------------------------
export async function generateCaptions(data) {
  const startedAt = Date.now();

  try {
    const payload = await attachBrandVoice(data);

    const response = await fetch(API_ENDPOINT, {
      method: "POST",
      headers: await jsonAuthHeaders(),
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      let errorBody;
      try {
        errorBody = await response.json();
      } catch {
        errorBody = { message: await response.text() };
      }

      const errMsg =
        errorBody?.message ||
        errorBody?.detail ||
        errorBody?.error ||
        "Failed to generate captions";

      safeCapture("generate_captions_failed", {
        status: response.status,
        message: String(errMsg).slice(0, 120),
        ms: Date.now() - startedAt,
      });

      throw new Error(String(errMsg));
    }

    const captions = await response.json();

    safeCapture("generate_captions", {
      ms: Date.now() - startedAt,
      count: Array.isArray(captions) ? captions.length : undefined,
    });

    return captions;
  } catch (error) {
    safeCapture("generate_captions_failed", {
      message: String(error?.message || "unknown").slice(0, 120),
      ms: Date.now() - startedAt,
    });

    console.error("API Error (captions):", error);
    throw error;
  }
}

// -----------------------------
// CAPTIONS (caption history route)
// -----------------------------
//
// The captions screen's path: POST /api/captions/social. The browser sends only
// the brand it picked, the brief and one platform; the worker resolves the
// brand's workspace, the caller's authority and the approved context, and saves
// the captions before returning them (src/utils/captionsSocial.js). There is no
// fallback to /remix, which generateCaptions above still calls, unchanged.
const CAPTIONS_SOCIAL_ENDPOINT = `${API_BASE}/captions/social`;

const socialCaptions = createSocialCaptionsClient({
  endpoint: CAPTIONS_SOCIAL_ENDPOINT,
  fetch: (...args) => fetch(...args),
  headers: () => jsonAuthHeaders(),
});

// Analytics carry the outcome only: never the brand, the brief or the captions.
export async function generateSocialCaptions(request) {
  const startedAt = Date.now();
  try {
    const result = await socialCaptions(request);
    safeCapture("generate_captions", {
      route: "captions_social",
      ms: Date.now() - startedAt,
      count: result.captions.length,
    });
    return result;
  } catch (error) {
    safeCapture("generate_captions_failed", {
      route: "captions_social",
      code: String(error?.code || "unknown").slice(0, 40),
      status: error?.status ?? undefined,
      ms: Date.now() - startedAt,
    });
    throw error;
  }
}

// The captions screen's brand picker: GET /api/captions/brands lists the brands the
// worker's caption rule lets this caller generate for -- including brands shared
// through brand memberships, which the browser cannot read itself -- as id and name
// only. Not analysed or logged here.
const CAPTION_BRANDS_ENDPOINT = `${API_BASE}/captions/brands`;

export const listCaptionBrands = createCaptionBrandsClient({
  endpoint: CAPTION_BRANDS_ENDPOINT,
  fetch: (...args) => fetch(...args),
  headers: () => getAuthHeaders(),
});

// -----------------------------
// BLOGS
// -----------------------------
export async function generateBlog(data) {
  const startedAt = Date.now();

  try {
    const payload = await attachBrandVoice(data);

    const response = await fetch(BLOG_ENDPOINT, {
      method: "POST",
      headers: await jsonAuthHeaders(),
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      let errorBody;
      try {
        errorBody = await response.json();
      } catch {
        errorBody = { message: await response.text() };
      }

      const errMsg =
        errorBody?.message ||
        errorBody?.detail ||
        errorBody?.error ||
        "Failed to generate blog";

      safeCapture("generate_blog_failed", {
        status: response.status,
        message: String(errMsg).slice(0, 120),
        ms: Date.now() - startedAt,
      });

      throw new Error(String(errMsg));
    }

    const blog = await response.json();

    safeCapture("generate_blog", {
      ms: Date.now() - startedAt,
    });

    return blog;
  } catch (error) {
    safeCapture("generate_blog_failed", {
      message: String(error?.message || "unknown").slice(0, 120),
      ms: Date.now() - startedAt,
    });

    console.error("API Error (blog):", error);
    throw error;
  }
}

// -----------------------------
// PREWRITING
// -----------------------------
/**
 * Run a Pre-Writing tool (topic finder, headline analyzer, seo titles, etc.)
 * data.tool is required (e.g. "topicFinder", "headline", "seoTitleMeta")
 */
export async function runPrewritingTool(data) {
  const startedAt = Date.now();

  try {
    const payload = await attachBrandVoice(data);

    const response = await fetch(PREWRITING_ENDPOINT, {
      method: "POST",
      headers: await jsonAuthHeaders(),
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      let errorBody;
      try {
        errorBody = await response.json();
      } catch {
        errorBody = { message: await response.text() };
      }

      const errMsg =
        errorBody?.message ||
        errorBody?.detail ||
        errorBody?.error ||
        "Failed to run pre-writing tool";

      safeCapture("prewriting_failed", {
        tool: payload?.tool || "unknown",
        status: response.status,
        message: String(errMsg).slice(0, 120),
        ms: Date.now() - startedAt,
      });

      throw new Error(String(errMsg));
    }

    const out = await response.json();

    safeCapture("prewriting_success", {
      tool: payload?.tool || "unknown",
      ms: Date.now() - startedAt,
    });

    return out;
  } catch (error) {
    safeCapture("prewriting_failed", {
      tool: data?.tool || "unknown",
      message: String(error?.message || "unknown").slice(0, 120),
      ms: Date.now() - startedAt,
    });

    console.error("API Error (prewriting):", error);
    throw error;
  }
}

// -----------------------------
// PREWRITING (tool wrappers)
// -----------------------------
export const prewriteTopicSparks = (payload) =>
  runPrewritingTool({ ...payload, tool: "topicFinder" });

export const headlineAnalyze = (payload) =>
  runPrewritingTool({ ...payload, tool: "headline" });

export const seoTitleMeta = (payload) =>
  runPrewritingTool({ ...payload, tool: "seoTitleMeta" });

// -----------------------------
// IMAGES (quality + optional reference file)
// -----------------------------
export async function generateImage(
  prompt,
  email,
  aspect,
  referenceFile,
  quality = "SD"
) {
  const startedAt = Date.now();

  const q = (quality || "SD").toUpperCase() === "HD" ? "HD" : "SD";
  const hasReference = !!referenceFile;

  try {
    const profile = await getProfile();
    const brandVoice = profile?.brand_voice || "";
    const brandProfile = profile?.active_voice_profile || null;

    let res;

    if (hasReference) {
      const formData = new FormData();
      formData.append("email", email);
      formData.append("prompt", prompt);
      formData.append("quality", q);
      if (aspect) formData.append("aspect", aspect);
      formData.append("reference", referenceFile);

      if (brandVoice) {
        formData.append("brand_voice", brandVoice);
      }

      if (brandProfile) {
        formData.append("brand_profile", JSON.stringify(brandProfile));
      }

      res = await fetch(IMAGES_ENDPOINT, {
        method: "POST",
        headers: await getAuthHeaders(),
        body: formData,
      });
    } else {
      res = await fetch(IMAGES_ENDPOINT, {
        method: "POST",
        headers: await jsonAuthHeaders(),
        body: JSON.stringify({
          email,
          prompt,
          aspect: aspect || "1:1",
          quality: q,
          brand_voice: brandVoice,
          brand_profile: brandProfile,
        }),
      });
    }

    let data = null;
    let rawText = "";
    try {
      data = await res.json();
    } catch {
      rawText = await res.text().catch(() => "");
    }

    if (!res.ok) {
      const errCode = data?.error || `http_${res.status}`;
      const errMsg =
        data?.message || data?.detail || rawText || "Image generation failed";

      safeCapture("generate_image_failed", {
        status: res.status,
        code: String(errCode).slice(0, 80),
        message: String(errMsg).slice(0, 120),
        quality: q,
        aspect: String(aspect || "").slice(0, 12),
        has_reference: hasReference,
        ms: Date.now() - startedAt,
      });

      if (
        errCode === "insufficient_credits" ||
        errCode === "pro_credits_exhausted"
      ) {
        throw new Error(
          data?.message || "Not enough credits to generate this image."
        );
      }

      throw new Error(String(errMsg || "Image generation failed"));
    }

    if (!data?.imageUrl) {
      safeCapture("generate_image_failed", {
        status: res.status,
        code: "no_imageUrl",
        message: "Image API did not return imageUrl",
        quality: q,
        aspect: String(aspect || "").slice(0, 12),
        has_reference: hasReference,
        ms: Date.now() - startedAt,
      });

      throw new Error("Image API did not return imageUrl");
    }

    safeCapture("generate_image", {
      ms: Date.now() - startedAt,
      quality: q,
      aspect: String(aspect || "").slice(0, 12),
      has_reference: hasReference,
    });

    return data.imageUrl;
  } catch (err) {
    console.error("API Error (image):", err);
    throw err;
  }
}